import {
	Component,
	forwardRef,
	AfterViewInit,
	ViewChild,
	Input,
} from '@angular/core';
import { CompanyAddress, Address } from 'src/shared/api';
import {
	NG_VALUE_ACCESSOR,
	ControlValueAccessor,
	FormGroup,
	FormArray,
	FormBuilder,
	AbstractControl,
} from '@angular/forms';
import { SelfDestruct } from 'src/shared/utilities/self-destruct';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { AbstractControlDataSource } from 'src/shared/form-models/abstract-control-data-source';
import { takeUntil, filter } from 'rxjs/operators';
import {
	CompanyAddressDialogComponent,
	companyAddressFormConfig,
} from '../company-address-dialog/company-address-dialog.component';
import { animateList } from 'src/shared/animations';

@Component({
	selector: 'rpshared-company-address-list',
	templateUrl: './company-address-list.component.html',
	styleUrls: ['./company-address-list.component.scss'],
	animations: [animateList],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => CompanyAddressListComponent),
			multi: true,
		},
	],
})
export class CompanyAddressListComponent extends SelfDestruct
	implements ControlValueAccessor, AfterViewInit {
	@Input() showRequiredError = false;
	@ViewChild(MatSort, { static: true }) sort: MatSort;
	@ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

	emptyColumns = ['empty'];
	resultColumns = ['address', 'isDefault', 'edit', 'delete'];

	parent = new FormGroup({
		array: new FormArray([]),
	});

	dataSource = new AbstractControlDataSource<CompanyAddress>(this.array);

	constructor(
		private readonly fb: FormBuilder,
		private readonly dialog: MatDialog
	) {
		super();
	}

	ngAfterViewInit() {
		this.dataSource.sort = this.sort;
		this.dataSource.paginator = this.paginator;
	}

	// model --> view
	writeValue(value: CompanyAddress[]): void {
		if (value) {
			this.array.clear();
			value.forEach((v) => {
				const fc = this.fb.group(companyAddressFormConfig) as FormGroup;
				fc.setValue(v);
				this.array.push(fc);
			});
		}
	}

	// view --> model
	registerOnChange(fn: (value: CompanyAddress[]) => void) {
		this.array.valueChanges.pipe(takeUntil(this.destroyed)).subscribe(fn);
	}

	registerOnTouched(fn: () => void) {
		this.onTouched = fn;
	}

	onTouched: () => void = () => {};

	addAddress() {
		const isFirstAddress = this.array.length === 0;
		const dialogRef = this.dialog.open<CompanyAddressDialogComponent, never, FormGroup>(
			CompanyAddressDialogComponent,
			{ width: '450px' }
		);
		dialogRef.afterClosed()
			.pipe(filter<AbstractControl>(Boolean))
			.subscribe((control) => {
				if (isFirstAddress) {
					control.patchValue({ isDefault: true });
				}
				this.array.insert(0, control);
			});
	}

	editAddress(companyAddress: CompanyAddress) {
		const dialogRef = this.dialog.open<CompanyAddressDialogComponent, CompanyAddress, FormGroup>(
			CompanyAddressDialogComponent,
			{ width: '450px', data: companyAddress }
		);
		dialogRef.afterClosed()
			.pipe(filter<AbstractControl>(Boolean))
			.subscribe((control) => {
				this.array.setControl(this.findAddressIndex(companyAddress), control);
			});
	}

	setAsDefault(companyAddress: CompanyAddress) {
		this.array.controls.forEach((c) => {
			c.patchValue({ isDefault: c.value === companyAddress }, { emitEvent: false });
		});
		this.array.updateValueAndValidity();
	}

	formatAddress(address: Address): string {
		if (!address) {
			return '';
		}
		return `${address.address1 || ''}, ${address.city || ''}, ${address.state || ''} ${address.zipCode || ''}`;
	}

	findAddressIndex(companyAddress: CompanyAddress): number {
		if (companyAddress.id) {
			return this.array.value.findIndex(
				(a: CompanyAddress) => a.id === companyAddress.id
			);
		}
		return this.array.value.findIndex((a: CompanyAddress) => a === companyAddress);
	}

	get array() {
		return this.parent.get('array') as FormArray;
	}
}
